import { useContext, useEffect, useState } from "react";
import toastMessages from "../../services/utils/helpers/toastMessage";
import useGetTermsAndConditions from "../../services/utils/custom hooks/Documents/useGetTermsAndConditions";
import Button from "../organism/Button";
import { PopUpContext } from "../../services/context/popupContext";
import { useHistory } from "react-router-dom";
import { useUpdateProfileMutation } from "../../services/utils/custom hooks/useUpdateProfile";
import { Formik } from "formik";
import CustomCheckBox from "../atomic/CustomCheckBox";
import moment from "moment";
import useAdBlockDetector from "../global/DetectAdBlock";
import { performLogout } from "../../services/session/performLogout";

function TermsAndConditionsPopup({
  oktaAuth,
  id,
  termsAccepted,
  profileData,
  closePopup,
}: Readonly<{
  oktaAuth: any;
  id: string;
  termsAccepted: boolean;
  profileData: any;
  closePopup?: Function;
}>): JSX.Element {
  const [documentLink, setDocumentLink] = useState<string>("");
  const { setVisible, setHideClose } = useContext(PopUpContext);
  const history = useHistory();
  const [cancelLoading, setCancelLoading] = useState(false);
  const { mutation, updating } = useUpdateProfileMutation(oktaAuth);
  const AdBlockerEnabled = useAdBlockDetector();
  const [isMobile, setIsMobile] = useState(
    typeof window !== "undefined" ? window.innerWidth < 768 : false
  );

  const getTermsAndConditions = useGetTermsAndConditions();
  const [fetchingDocument, setFetchingDocument] = useState(false);

  const showTermsAndConditions = !termsAccepted && !fetchingDocument;
  const showFallbackWarning =
    !fetchingDocument && !!documentLink && (AdBlockerEnabled || isMobile);

  useEffect(() => {
    const updateMobileState = () => {
      setIsMobile(window.innerWidth < 768);
    };

    if (typeof window !== "undefined") {
      window.addEventListener("resize", updateMobileState);
    }

    return () => {
      if (typeof window !== "undefined") {
        window.removeEventListener("resize", updateMobileState);
      }
    };
  }, []);

  useEffect(() => {
    if (AdBlockerEnabled) {
      toastMessages.warning(
        "Ad Blocker Detected. Please disable your ad blocker to use all features of the application"
      );
    }
  }, [AdBlockerEnabled]);

  async function handleViewURL() {
    setFetchingDocument(true);
    try {
      const response = await getTermsAndConditions.mutateAsync(id);
      const data = response.data?.data || [];
      
      let url = "";
      if (id === "isAltheaDxNeuropsychotropicMedications" && data[0]?.blobUrl) {
        url = data[0].blobUrl;
      } else {
        const termOfUse = data.find((file: any) => file?.type === "term-of-use");
        url = termOfUse?.blobUrl || "";
      }

      setDocumentLink(url);
      setFetchingDocument(false);
    } catch (error: any) {
      toastMessages.error("Failed to open Terms and Conditions");
      setFetchingDocument(false);
    }
  }

  const logout = async () => {
    setCancelLoading(true);
    try {
      await performLogout({
        oktaAuth: oktaAuth?.oktaAuth,
        history,
      });
    } catch {
      toastMessages.error("Logout failed. Please try again.");
    } finally {
      setCancelLoading(false);
    }
  };

  useEffect(() => {
    if (mutation.isSuccess) {
      setVisible(false);
      setHideClose(false);
      if (closePopup) {
        closePopup(); // Call closePopup to close TermsAndConditionsPopup and open NewReleasePopup
      }
    }
  }, [mutation.isSuccess]);

  useEffect(() => {
    handleViewURL();
  }, []);

  return (
    <div className="min-w-[35vw] md:h-[80vh] lg:min-w-[30vw] overflow-auto">
      {showFallbackWarning && (
        <div className="p-6 bg-yellow-50 border-l-4 border-yellow-400 rounded-lg shadow-sm">
          <div className="flex items-start">
            <div className="ml-3">
              <h3 className="text-lg font-medium text-yellow-800">
                PDF Viewer Blocked
              </h3>
              <div className="mt-2 text-sm text-yellow-700">
                <p>
                  {AdBlockerEnabled
                    ? "We've detected an ad blocker that may be preventing the document from displaying properly. Please open the document in a new tab."
                    : "PDF preview is not supported on mobile. Please open the document in a new tab."}
                </p>
                <div className="mt-4">
                  <a
                    href={documentLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center px-4 py-2 bg-primary hover:shadow-primaryHover hover:bg-primaryHover shadow-sm shadow-primary rounded-md uppercase text-white text-sm font-semibold focus:outline-none focus:ring-2 focus:ring-offset-2"
                  >
                    Open PDF in New Tab
                  </a>
                </div>
                <p className="mt-4 text-yellow-600">
                  Still having trouble?{" "}
                  <a
                    href="mailto:clinicalservices@castlebiosciences.com"
                    className="font-medium underline hover:text-yellow-500"
                  >
                    Contact support
                  </a>{" "}
                  {AdBlockerEnabled
                    ? "or try disabling your ad blocker."
                    : "or try opening the document from a desktop browser."}
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {!fetchingDocument && !!documentLink && !AdBlockerEnabled && !isMobile && (
        <embed
          width={250}
          height={250}
          src={documentLink}
          className={`h-[80vh] ${showTermsAndConditions ? "md:h-[70vh]" : "md:h-[77vh]"} w-full object-contain`}
        />
      )}
      {fetchingDocument && (
        <div className="flex h-[30vh] w-full justify-center items-center">
          <div className="animate-spin">
            <img
              src={require("../../assets/images/loadingPNG.png")}
              className={"h-[50px] w-[50px]"}
              alt="loading indicator"
            />
          </div>
        </div>
      )}
      {showTermsAndConditions && (
        <Formik
          initialValues={{
            firstName: profileData?.user?.firstName ?? "",
            email_id: profileData?.user?.email ?? "",
            lastName: profileData?.user?.lastName ?? "",
            acceptTerms: false,
            acceptTermsDate: moment().format("MM-DD-YYYY"),
          }}
          onSubmit={(data) => {
            mutation.mutate(data);
          }}
        >
          {({ values, errors, getFieldMeta, submitForm }) => {
            return (
              <div className="flex justify-between items-end mt-4 flex-wrap md:flex-nowrap">
                <CustomCheckBox
                  id="acceptTerms"
                  name="acceptTerms"
                  value={values.acceptTerms}
                  label="I have agreed to the Terms of Use."
                />
                <div className="flex items-center md:mt-0 mt-2">
                  <Button
                    textInformation={{
                      text: "Cancel",
                      fontWeight: "font-semibold",
                    }}
                    buttonTheme="secondary"
                    disabled={values.acceptTerms || updating || cancelLoading}
                    onClick={logout}
                    loading={cancelLoading}
                    className="mr-2"
                  />
                  <Button
                    textInformation={{
                      text: "Ok",
                      fontWeight: "font-semibold",
                    }}
                    buttonTheme="primary"
                    disabled={!values.acceptTerms || updating}
                    loading={updating}
                    onClick={submitForm}
                  />
                </div>
              </div>
            );
          }}
        </Formik>
      )}
    </div>
  );
}

export default TermsAndConditionsPopup;
